import { ReactNode } from "react";
import {
  ArrowDownCircleIcon,
  ChartPieIcon,
  // ClipboardIcon and BsFileBarGraph (react-icons/bs) below were unused dead imports even before
  // this rewrite — dropped per bulk-campaign-report-ui-port Task 5 brief Step 5.
  // ClipboardIcon,
  EllipsisHorizontalIcon,
  PauseIcon,
  PlayIcon,
  TrashIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { Tooltip } from "antd";
import {
  Dropdown,
  DropdownButton,
  DropdownMenu,
  DropdownItem,
} from "V2.0/common/dropdown";
import {
  BULK_CAMPAIGN_STATUS,
  BulkCampaignStatus,
} from "../../BulkCampaignStatus";
// import { BsFileBarGraph } from "react-icons/bs";

export default BulkCampaignActions;

const ACTION_BUTTON_CLASSNAME =
  "flex size-8 shrink-0 items-center justify-center rounded-full border border-zinc-200 bg-zinc-50 text-zinc-500 shadow-sm transition-all duration-150 hover:-translate-y-0.5 hover:border-indigo-200 hover:bg-indigo-50 hover:text-indigo-600 hover:shadow-md active:translate-y-0 active:shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-1 dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-400 dark:hover:border-indigo-800 dark:hover:bg-indigo-950 dark:hover:text-indigo-300";

interface BulkCampaignActionsProps {
  bulkCampaignStatus: BulkCampaignStatus;
  onBulkCampaignDelete: () => void;
  onBulkCampaignDownload: () => void;
  onBulkCampaignResume: () => void;
  onBulkCampaignPause: () => void;
  onBulkCampaignCancel: () => void;
  onBulkCampaignReport: () => void;
  isViewOnly: boolean;
}

type Action = {
  label: string;
  icon: ReactNode;
  actionHandler: VoidFunction;
  showAction: boolean;
};

function BulkCampaignActions(props: BulkCampaignActionsProps) {
  const {
    onBulkCampaignDownload,
    onBulkCampaignDelete,
    onBulkCampaignResume,
    onBulkCampaignPause,
    onBulkCampaignCancel,
    onBulkCampaignReport,
    bulkCampaignStatus,
    isViewOnly,
  } = props;

  // Old flat `actions` array rendered as a single Dropdown (EllipsisHorizontalIcon button) listing
  // every action. Replaced below with a primary/secondary split — standalone icon buttons for the
  // most-used actions, with the rest collapsing into an overflow menu only once there'd be more
  // than 3 buttons total. (bulk-campaign-report-ui-port, Task 5)
  // const actions: Array<Action> = [
  //   {
  //     label: "Download",
  //     icon: <ArrowDownCircleIcon />,
  //     actionHandler: onBulkCampaignDownload,
  //     showAction: true,
  //   },
  //   {
  //     label: "Resume",
  //     icon: <PlayIcon />,
  //     actionHandler: onBulkCampaignResume,
  //     showAction: !isViewOnly && [
  //       BULK_CAMPAIGN_STATUS.PAUSED,
  //       BULK_CAMPAIGN_STATUS.QUOTA_EXHAUSTED,
  //     ].some((status) => status === bulkCampaignStatus),
  //   },
  //   {
  //     label: "Pause",
  //     icon: <PauseIcon />,
  //     actionHandler: onBulkCampaignPause,
  //     showAction: !isViewOnly && [
  //       BULK_CAMPAIGN_STATUS.ONGOING,
  //       BULK_CAMPAIGN_STATUS.PROCESSING,
  //     ].some((status) => status === bulkCampaignStatus),
  //   },
  //   {
  //     label: "Report",
  //     icon: <ChartPieIcon />,
  //     showAction: !isViewOnly,
  //     actionHandler: onBulkCampaignReport,
  //   },
  //   {
  //     label: "Cancel",
  //     icon: <XMarkIcon />,
  //     actionHandler: onBulkCampaignCancel,
  //     showAction: !isViewOnly && [
  //       BULK_CAMPAIGN_STATUS.COMPLETED,
  //       BULK_CAMPAIGN_STATUS.CANCELLED,
  //     ].every((status) => status != bulkCampaignStatus),
  //   },
  //   {
  //     label: "Delete",
  //     icon: <TrashIcon />,
  //     showAction: !isViewOnly,
  //     actionHandler: onBulkCampaignDelete,
  //   },
  // ];

  // Always-visible, non-destructive actions. Kept as standalone icon buttons since these are
  // the ones used most often and every row shows both regardless of status.
  //
  // DEVIATION FROM FRONTEND (bulk-campaign-report-ui-port, Task 5 brief Step 5, required): Frontend
  // made Report's showAction unconditionally true when it became a primary action. Catalyst keeps
  // its existing RBAC gate (!isViewOnly) on Report instead — do not copy Frontend's unconditional
  // visibility here.
  const primaryActions: Array<Action> = [
    {
      label: "Download",
      icon: <ArrowDownCircleIcon className="size-4" />,
      actionHandler: onBulkCampaignDownload,
      showAction: true,
    },
    {
      label: "Report",
      icon: <ChartPieIcon className="size-4" />,
      actionHandler: onBulkCampaignReport,
      showAction: !isViewOnly,
    },
  ];

  // State-changing actions, whose count varies with bulkCampaignStatus (up to 4 at once for
  // an ongoing campaign). Only collapsed into a "More options" menu once they'd otherwise push
  // the row past 3 buttons total — below that threshold they render inline same as before.
  const secondaryActions: Array<Action> = [
    {
      label: "Resume",
      icon: <PlayIcon className="size-4" />,
      actionHandler: onBulkCampaignResume,
      showAction: !isViewOnly && [
        BULK_CAMPAIGN_STATUS.PAUSED,
        BULK_CAMPAIGN_STATUS.QUOTA_EXHAUSTED,
      ].some((status) => status === bulkCampaignStatus),
    },
    {
      label: "Pause",
      icon: <PauseIcon className="size-4" />,
      actionHandler: onBulkCampaignPause,
      showAction: !isViewOnly && [
        BULK_CAMPAIGN_STATUS.ONGOING,
        BULK_CAMPAIGN_STATUS.PROCESSING,
      ].some((status) => status === bulkCampaignStatus),
    },
    {
      label: "Cancel",
      icon: <XMarkIcon className="size-4" />,
      actionHandler: onBulkCampaignCancel,
      showAction: !isViewOnly && [
        BULK_CAMPAIGN_STATUS.COMPLETED,
        BULK_CAMPAIGN_STATUS.CANCELLED,
      ].every((status) => status != bulkCampaignStatus),
    },
    {
      label: "Delete",
      icon: <TrashIcon className="size-4" />,
      showAction: !isViewOnly,
      actionHandler: onBulkCampaignDelete,
    },
  ].filter((action) => action.showAction);

  // Below the threshold, secondaryActions renders flat alongside primaryActions exactly as
  // it used to; only crossing it switches those buttons into the overflow menu.
  const useOverflowMenu = primaryActions.length + secondaryActions.length > 3;

  return (
    <div
      className="flex items-center justify-end gap-x-1.5"
      onClick={(e) => e.stopPropagation()}
    >
      {primaryActions.map((action) => {
        const { showAction, actionHandler, label, icon } = action;

        if (!showAction) return null;

        return (
          <Tooltip
            key={label}
            title={label}
          >
            <button
              type="button"
              className={ACTION_BUTTON_CLASSNAME}
              onClick={actionHandler}
            >
              {icon}
            </button>
          </Tooltip>
        );
      })}

      {useOverflowMenu ? (
        <Dropdown>
          <DropdownButton
            as="button"
            type="button"
            className={ACTION_BUTTON_CLASSNAME}
            aria-label="More options"
          >
            <EllipsisHorizontalIcon className="size-4" />
          </DropdownButton>
          <DropdownMenu anchor="bottom end">
            {secondaryActions.map(({ label, icon, actionHandler }) => (
              <DropdownItem
                key={label}
                onClick={actionHandler}
              >
                {icon}
                {label}
              </DropdownItem>
            ))}
          </DropdownMenu>
        </Dropdown>
      ) : (
        secondaryActions.map(({ label, icon, actionHandler }) => (
          <Tooltip
            key={label}
            title={label}
          >
            <button
              type="button"
              className={ACTION_BUTTON_CLASSNAME}
              onClick={actionHandler}
            >
              {icon}
            </button>
          </Tooltip>
        ))
      )}
    </div>
  );
}
